import { supabase } from './supabaseClient';

// ============================================================
// Media pentru compozitorul v2.
//
// Modul propriu, ca tot restul lui v2. Ajutoarele de media ale compozitorului
// vechi traiesc inauntrul functiei Compositor din App.js, deci nu se pot importa
// oricum. Aici sunt scrise din nou, dar cu ACELEASI conventii, si asta nu e
// optional: daca fisierele ar ajunge in alt depozit sau pe alta cale, functiile
// de publicare nu le-ar mai gasi.
//
// Depozit: bucket-ul `media-clienti`.
// Calea: {client_id}/postari/{timp}_{aleator}.{extensie}
// ============================================================

const DEPOZIT = 'media-clienti';

// Plafonul dupa care se micsoreaza o poza la incarcare. 2560 pe latura lunga e
// peste ce afiseaza oricare dintre retele, care oricum recomprima la ele, deci
// nu se pierde nimic vizibil; machetele de 1080x1350 nu sunt atinse niciodata.
//
// Tinta in MB e 4,5, adica sub cei 5 MB pe care LinkedIn ii scrie in pagina lor
// de ajutor, cu o margine. Un singur prag pentru toate retelele, nu cate unul pe
// canal: la 2560 si calitate 0,9 o poza iese tipic sub 1,5 MB, deci pragul lor
// cel mai strans e oricum respectat, iar cel de 50 MB al depozitului nostru nici
// nu se apropie. Doua praguri ar fi complicat codul fara sa schimbe vreun fisier.
const LATURA_MAXIMA_INCARCARE = 2560;
const TINTA_MB = 4.5;
const CALITATI = [0.92, 0.85, 0.78, 0.7, 0.62];

function subTinta(blob) { return blob && blob.size / (1024 * 1024) <= TINTA_MB; }

// Instagram respinge PNG cu transparenta si o randeaza cu fundal negru. Se
// converteste in JPEG, umpland intai cu alb, altfel transparenta devine neagra.
// Aceeasi trecere prin panza micsoreaza si pozele prea mari: fotografiile venite
// de la telefon au 4000 de pixeli si 10-12 MB, iar plafonul LinkedIn e 5 MB. Fara
// asta, singura data cand aflai marimea era cand ceva pica.
//
// Un JPEG mic si de dimensiuni rezonabile trece NEATINS: o recodare inutila
// pierde calitate degeaba.
export function laJpeg(fisier) {
  return new Promise((rezolva, respinge) => {
    const obiectUrl = URL.createObjectURL(fisier);
    const img = new Image();
    img.onload = async () => {
      URL.revokeObjectURL(obiectUrl);
      const L = img.naturalWidth, I = img.naturalHeight;
      const laturaLunga = Math.max(L, I);
      const mb = fisier.size / (1024 * 1024);
      const preaMare = laturaLunga > LATURA_MAXIMA_INCARCARE || mb > TINTA_MB;

      if (fisier.type === 'image/jpeg' && !preaMare) {
        rezolva({ fisier, convertit: false, micsorat: false });
        return;
      }

      const scara = laturaLunga > LATURA_MAXIMA_INCARCARE ? LATURA_MAXIMA_INCARCARE / laturaLunga : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(L * scara));
      canvas.height = Math.max(1, Math.round(I * scara));
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Calitatea coboara doar cat trebuie. La majoritatea pozelor se opreste din
      // prima treapta; treptele de jos exista pentru fotografiile foarte
      // incarcate, unde 0,92 tot nu incape in tinta.
      let blob = null;
      let calitateFolosita = CALITATI[0];
      for (const calitate of CALITATI) {
        // eslint-disable-next-line no-await-in-loop
        blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', calitate));
        calitateFolosita = calitate;
        if (subTinta(blob)) break;
      }
      if (!blob) { respinge(new Error('conversie eșuată')); return; }

      const nume = fisier.name.replace(/\.[^.]+$/, '') + '.jpg';
      rezolva({
        fisier: new File([blob], nume, { type: 'image/jpeg' }),
        convertit: fisier.type !== 'image/jpeg',
        micsorat: preaMare,
        calitate: calitateFolosita,
      });
    };
    img.onerror = () => { URL.revokeObjectURL(obiectUrl); respinge(new Error('imagine ilizibilă')); };
    img.src = obiectUrl;
  });
}

// Dimensiunile reale ale imaginii. Raportul e cel dupa care Instagram decide
// daca taie: se foloseste la avertismente, in 2b.
export function masoaraImagine(fisier) {
  return new Promise(rezolva => {
    const url = URL.createObjectURL(fisier);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      rezolva({ latime: img.naturalWidth, inaltime: img.naturalHeight, raport: img.naturalWidth / Math.max(1, img.naturalHeight) });
    };
    img.onerror = () => { URL.revokeObjectURL(url); rezolva({ latime: 0, inaltime: 0, raport: 0 }); };
    img.src = url;
  });
}

// Durata SI dimensiunile videoului. Dimensiunile conteaza: un video prea mic e
// acceptat de Facebook la incarcare, procesat, si apoi aruncat in tacere.
// Daca browserul nu le poate citi, intoarce zerouri si nu blocheaza nimic —
// mai bine fara avertisment decat cu unul fals.
export function masoaraVideo(fisier) {
  return new Promise(rezolva => {
    const url = URL.createObjectURL(fisier);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      rezolva({
        durataSec: Number.isFinite(v.duration) ? v.duration : 0,
        latime: v.videoWidth || 0,
        inaltime: v.videoHeight || 0,
      });
    };
    v.onerror = () => { URL.revokeObjectURL(url); rezolva({ durataSec: 0, latime: 0, inaltime: 0 }); };
    v.src = url;
  });
}

function caleNoua(clientId, fisier) {
  const extensie = (fisier.name.split('.').pop() || 'bin').toLowerCase();
  const nume = `${Date.now()}_${Math.random().toString(36).slice(2)}.${extensie}`;
  return `${parseInt(clientId, 10)}/postari/${nume}`;
}

export async function urcaInDepozit(clientId, fisier) {
  const cale = caleNoua(clientId, fisier);
  const { error } = await supabase.storage.from(DEPOZIT).upload(cale, fisier);
  if (error) throw error;
  const { data } = supabase.storage.from(DEPOZIT).getPublicUrl(cale);
  return { url: data.publicUrl, cale };
}

let contor = 0;

// Un fisier ales de om devine un element de media: intai cu previzualizare
// locala, ca sa se vada instant, apoi cu adresa publica, dupa ce urca.
//
// Previzualizarea arata rezultatul REAL, nu originalul. Daca s-a convertit din
// PNG, omul trebuie sa vada fundalul alb pe ecran, nu transparenta, altfel
// descopera schimbarea abia in postarea publicata.
export async function pregatesteFisier(clientId, fisier) {
  const esteImagine = fisier.type.startsWith('image/');
  const esteVideo = fisier.type.startsWith('video/');
  if (!esteImagine && !esteVideo) return null;

  contor += 1;
  const baza = {
    id: `m${Date.now()}_${contor}`,
    tip: esteImagine ? 'imagine' : 'video',
    nume: fisier.name,
    marimeMB: fisier.size / (1024 * 1024),
    url: null,
    cale: null,
    convertit: false,
    eroare: null,
    latime: 0, inaltime: 0, raport: 0, durataSec: 0,
  };

  if (esteImagine) {
    const { fisier: deIncarcat, convertit, micsorat, calitate } = await laJpeg(fisier);
    const masuri = await masoaraImagine(deIncarcat);
    const mbDupa = deIncarcat.size / (1024 * 1024);
    // Micsorarea obisnuita NU se anunta. E o operatie de casa, mereu sigura, care
    // nu schimba nimic din ce se vede la publicare; un mesaj acolo e zgomot, si
    // zgomotul invata omul sa nu mai citeasca avertismentele deloc.
    // Se anunta doar comprimarea DURA, adica atunci cand calitatea a trebuit sa
    // coboare sub 0,8 ca poza sa intre in tinta. Acolo chiar se poate vedea, mai
    // ales pe un afis cu text marunt, si omul are ce decide.
    const comprimareDura = micsorat && calitate <= 0.78;
    return {
      ...baza,
      ...masuri,
      convertit,
      micsorata: comprimareDura ? { de: baza.marimeMB, la: mbDupa } : null,
      marimeMB: mbDupa,
      previzualizare: URL.createObjectURL(deIncarcat),
      _deIncarcat: deIncarcat,
    };
  }

  const m = await masoaraVideo(fisier);
  return {
    ...baza,
    durataSec: m.durataSec,
    latime: m.latime,
    inaltime: m.inaltime,
    raport: m.inaltime ? m.latime / m.inaltime : 0,
    previzualizare: URL.createObjectURL(fisier),
    _deIncarcat: fisier,
  };
}

// ============================================================
// MEDIA PRIN LINK
//
// De ce exista: depozitul nostru taie la 50 MB (planul gratuit Supabase). Un
// link ocoleste plafonul cu totul — fisierul nu mai trece prin depozit, iar
// Facebook si Instagram se duc singure sa-l ia de la adresa. Verificat in
// codul de publicare pe 22 august: ele primesc doar `file_url` / `video_url`,
// niciodata fisierul.
//
// NUMAI VIDEO, deliberat. Pozele raman pe upload pentru ca trebuie convertite
// in JPEG cu fundal alb inainte de Instagram, iar asta cere fisierul in mana.
// Oricum pozele rareori trec de 50 MB, deci linkul n-ar rezolva nimic acolo.
// ============================================================

// Linkul de partajare duce la o PAGINA Dropbox, nu la fisier. Facebook ar
// primi HTML in loc de video. Adus din NM Write (index.html, commit 8fa2c00),
// unde face acelasi lucru din 17 august.
export function linkDirect(brut) {
  const text = String(brut || '').trim();
  try {
    const u = new URL(text);
    if (u.hostname.replace(/^www\./, '') === 'dropbox.com') {
      const rl = u.searchParams.get('rlkey');
      return 'https://dl.dropboxusercontent.com' + u.pathname + (rl ? '?rlkey=' + encodeURIComponent(rl) : '');
    }
  } catch (_e) { /* nu e adresa valida; se prinde mai jos */ }
  return text;
}

// Citeste dimensiunile si durata dintr-un MP4 aflat pe alt server, fara sa
// descarce filmul.
//
// Cutiile MP4 sunt o lista: fiecare isi spune marimea si tipul in primii 8
// octeti. Cea care ne trebuie e moov. La filmul de proba al Luminitei statea
// la octetul 48, deci am aflat 1280x720 si 55,5 secunde citind 64 KB dintr-un
// fisier de 12 MB. La alte filme moov sta la COADA, dupa datele video — de
// aia sarim din antet in antet cu cereri mici, in loc sa presupunem ca e la cap.
//
// Merge doar pentru ca Dropbox raspunde cu Accept-Ranges: bytes SI cu
// Access-Control-Allow-Origin: *. Ambele verificate pe 22 august. Alt server
// poate sa nu le dea; atunci masurarea pica si se spune pe ecran, nu se ascunde.
const FEREASTRA = 65536;      // cat citim din cap la prima cerere
const SARITURI_MAX = 8;       // cate antete sarim inainte sa renuntam
const MOOV_MAX = 4 * 1024 * 1024;

function bucata(dv, p) {
  let marime = dv.getUint32(p);
  const tip = String.fromCharCode(dv.getUint8(p + 4), dv.getUint8(p + 5), dv.getUint8(p + 6), dv.getUint8(p + 7));
  let antet = 8;
  if (marime === 1) {
    marime = dv.getUint32(p + 8) * 4294967296 + dv.getUint32(p + 12);
    antet = 16;
  }
  return { marime, tip, antet };
}

// Scoate durata si dimensiunile dintr-un moov intreg.
export function citesteMoov(ab) {
  const dv = new DataView(ab);
  let durataSec = 0, latime = 0, inaltime = 0;

  function mergi(inceput, sfarsit) {
    let p = inceput;
    while (p + 8 <= sfarsit) {
      const info = bucata(dv, p);
      const corp = p + info.antet;
      const pas = info.marime === 0 ? sfarsit - p : info.marime;
      if (pas < 8) return;

      if (info.tip === 'mvhd' && corp + 20 <= sfarsit) {
        const ver = dv.getUint8(corp);
        let q = corp + 4;
        let scara = 0, dur = 0;
        if (ver === 1) {
          q += 16; scara = dv.getUint32(q); q += 4;
          dur = dv.getUint32(q) * 4294967296 + dv.getUint32(q + 4);
        } else {
          q += 8; scara = dv.getUint32(q); q += 4; dur = dv.getUint32(q);
        }
        if (scara) durataSec = dur / scara;
      } else if (info.tip === 'tkhd') {
        const ver = dv.getUint8(corp);
        // versiune+stegulete(4), apoi timpii si durata (32 la v1, 20 la v0),
        // rezervat(8), layer/grup/volum/rezervat(8), matricea(36).
        const q = corp + 4 + (ver === 1 ? 32 : 20) + 8 + 8 + 36;
        if (q + 8 <= sfarsit) {
          const l = dv.getUint32(q) / 65536;
          const i = dv.getUint32(q + 4) / 65536;
          // Pista de sunet are 0x0. Prima pista cu dimensiuni e cea video.
          if (l && i && !latime) { latime = Math.round(l); inaltime = Math.round(i); }
        }
      } else if (info.tip === 'moov' || info.tip === 'trak' || info.tip === 'mdia') {
        mergi(corp, Math.min(p + pas, sfarsit));
      }
      p += pas;
    }
  }

  mergi(0, ab.byteLength);
  return { durataSec: durataSec, latime: latime, inaltime: inaltime };
}

async function cere(url, de, pana) {
  const r = await fetch(url, { headers: { Range: 'bytes=' + de + '-' + pana } });
  if (!r.ok) throw new Error('HTTP ' + r.status);
  const interval = r.headers.get('content-range') || '';
  const total = Number((interval.split('/')[1] || '').trim()) || 0;
  return { ab: await r.arrayBuffer(), total: total, tip: r.headers.get('content-type') || '' };
}

export async function masoaraVideoDeLaDistanta(url) {
  const cap = await cere(url, 0, FEREASTRA - 1);
  const total = cap.total;

  // Mergem din antet in antet pana dam de moov.
  let pozitie = 0;
  let tampon = cap.ab;
  let bazaTampon = 0;

  for (let pas = 0; pas < SARITURI_MAX; pas += 1) {
    if (pozitie - bazaTampon + 16 > tampon.byteLength) {
      if (total && pozitie >= total) break;
      const mic = await cere(url, pozitie, pozitie + 15);
      tampon = mic.ab; bazaTampon = pozitie;
      if (tampon.byteLength < 8) break;
    }
    const dv = new DataView(tampon);
    const info = bucata(dv, pozitie - bazaTampon);
    if (!info.marime || info.marime < 8) break;

    if (info.tip === 'moov') {
      const lung = Math.min(info.marime, MOOV_MAX);
      const inauntru = pozitie - bazaTampon + lung <= tampon.byteLength;
      const ab = inauntru
        ? tampon.slice(pozitie - bazaTampon, pozitie - bazaTampon + lung)
        : (await cere(url, pozitie, pozitie + lung - 1)).ab;
      const m = citesteMoov(ab.byteLength > info.antet ? ab.slice(info.antet) : ab);
      return {
        durataSec: m.durataSec, latime: m.latime, inaltime: m.inaltime,
        marimeMB: total ? total / (1024 * 1024) : 0, tip: cap.tip,
      };
    }
    pozitie += info.marime;
  }

  // N-am gasit moov: intoarcem ce stim (marimea), fara masuratori.
  return {
    durataSec: 0, latime: 0, inaltime: 0,
    marimeMB: total ? total / (1024 * 1024) : 0, tip: cap.tip,
  };
}

// Pregateste un element de media pornind de la un link, fara nicio urcare.
export async function pregatesteLink(brut) {
  const url = linkDirect(brut);
  if (!/^https:\/\//i.test(url)) return { eroare: 'linkNevalid' };

  let nume = 'video (link)';
  try { nume = decodeURIComponent(new URL(url).pathname.split('/').pop()) || nume; } catch (_e) { /* ramane implicit */ }

  contor += 1;
  const baza = {
    id: 'l' + Date.now() + '_' + contor,
    tip: 'video', nume: nume, url: url, cale: null, convertit: false, eroare: null,
    previzualizare: url, esteLink: true,
    latime: 0, inaltime: 0, raport: 0, durataSec: 0, marimeMB: 0, masurat: false,
  };

  try {
    const m = await masoaraVideoDeLaDistanta(url);
    if (m.tip && !m.tip.startsWith('video/')) return { eroare: 'linkNuEVideo' };
    return Object.assign({}, baza, {
      durataSec: m.durataSec, latime: m.latime, inaltime: m.inaltime,
      raport: m.inaltime ? m.latime / m.inaltime : 0,
      marimeMB: m.marimeMB,
      masurat: !!(m.latime && m.inaltime),
    });
  } catch (_e) {
    // Serverul n-a lasat sa citim. Linkul ramane folosibil — publicarea nu
    // trece prin browserul nostru — dar fara masuratori, si se spune.
    return baza;
  }
}

// Urca ce a fost pregatit si intoarce elementul cu adresa publica pusa.
// Eroarea se pastreaza PE element, nu se arunca: un fisier picat nu trebuie sa
// darame incarcarea celorlalte, iar omul trebuie sa vada care anume a picat.
export async function urcaElement(clientId, element) {
  try {
    const { url, cale } = await urcaInDepozit(clientId, element._deIncarcat);
    return { ...element, url, cale, eroare: null, _deIncarcat: undefined };
  } catch (err) {
    return { ...element, eroare: err.message || 'nu s-a putut urca' };
  }
}

export function elibereazaPrevizualizarea(element) {
  if (element && element.previzualizare && element.previzualizare.startsWith('blob:')) {
    URL.revokeObjectURL(element.previzualizare);
  }
}

// ============================================================
// Avertismente
//
// REGULA, decisa in august: avertismentul sare DOAR la probleme reale. Nu la
// orientare. Orizontalul e permis peste tot, inclusiv pe TikTok. Verticalul
// ramane de preferat la Story si Reels, dar nu se impune si nu se semnaleaza.
//
// Functie pura: primeste cheia si lista, intoarce mesaje. Se poate incerca in
// afara ecranului, si asa a si fost verificata.
// ============================================================

// Megabytes, per canal. Aceleasi cifre ca in compozitorul vechi (LIMITE_VIDEO).
// Threads: 1 GB si cel mult 5 minute, din documentatia lor (19 sept 2026).
// X: filmul trece prin memoria functiei `x-publish`, deci plafonul e cel al depozitului.
const LIMITA_VIDEO_MB = { facebook: 4096, instagram: 100, tiktok: 287, threads: 1024, x: 512 };

// Plafonul depozitului nostru, mai strans decat toate limitele de mai sus.
// Fisierul nu ajunge niciodata la Facebook sau TikTok direct: urca intai in
// Supabase, primeste o adresa publica, si abia de acolo si-l ia platforma. Deci
// limita care conteaza cu adevarat e a depozitului, nu a retelei.
//
// 50 MB e plafonul planului gratuit Supabase. Verificat pe 22 august 2026:
// bucket-ul „media-clienti" NU adauga o limita proprie („Restrict file size"
// stins), iar comutatorul global nici nu apare in panou — pe planul gratuit e
// fix si nu se poate schimba.
//
// LA TRECEREA PE PLAN PLATIT: se ridica la 50 GB si cifra de aici trebuie
// marita, altfel blocam incarcari care ar merge.
const LIMITA_DEPOZIT_MB = 50;

// TikTok are alt gat de sticla, si nu la el, ci la noi: `tiktok-publish`
// DESCARCA filmul in memoria functiei (`arrayBuffer()`) si abia apoi il urca.
// Facebook si Instagram nu fac asta — ele primesc adresa si se descurca singure.
//
// Deci pe TikTok limita nu e a retelei, e cata memorie are functia. Cifra e
// APROXIMATIVA, de aia avertismentul e galben, nu rosu: n-am masurat unde cade
// exact, si prefer sa las omul sa incerce decat sa-l opresc pe o presupunere.
// Prin depozit nu se atinge niciodata (50 MB); se atinge doar prin link.
const LIMITA_TIKTOK_MEMORIE_MB = 100;

// Ce forma cere fiecare CANAL x FORMAT. Pe canal nu ajunge: Instagram Feed
// accepta intre 0.8 si 1.91, dar Story-ul nu e un interval, e o forma unica —
// 9:16, pe tot ecranul. O poza de 4:5 trece la Feed si nu umple ecranul la Story.
//   Feed si Carusel: interval. Ce iese din el se decupeaza tacut la publicare.
//   Story: ecran plin, 9:16. Ce nu e aproape de 9:16 ori apare cu benzi, ori se
//          taie din laterale.
//   Reel: nu are intrare aici. Reelul cere video, iar o poza pe un card de Reel
//         e deja oprita mai sus, de cereVideo — un al doilea mesaj ar fi zgomot.
//   TikTok: nimic, deliberat. Decizia din august: orizontalul e permis peste tot,
//           inclusiv pe TikTok, fara avertisment pe orientare.
const TOLERANTA_ECRAN_PLIN = 0.05;
const RAPORT = {
  'facebook|Feed': { min: 0.5625, max: 1.91 },
  'instagram|Feed': { min: 0.8, max: 1.91 },
  'instagram|Carusel': { min: 0.8, max: 1.91 },
  // LinkedIn, verificat in documentatia lor pe 12 septembrie 2026: „The photo's
  // aspect ratio can range from 3:1 to 4:5 (width:height). If the ratio is larger,
  // the image will be centered and cropped to fit the max ratio."
  // Deci 0.8 (4:5, verticalul cel mai inalt acceptat) pana la 3 (3:1). Machetele
  // noastre de 1080x1350 sunt fix 0.8, adica exact la limita de sus, netaiate.
  'linkedin|Feed': { min: 0.8, max: 3 },
  'facebook|Story': { ecranPlin: 0.5625 },
  'instagram|Story': { ecranPlin: 0.5625 },
};

// Ce fel de media cere formatul. Verificarea nu e cosmetica: functia de publicare
// TikTok se uita intai dupa video si, daca nu gaseste, trimite pe drumul de poze.
// Deci un card care scrie „Video" si are o poza publica un Photo Mode — altceva
// decat scrie pe card. La Reel e si mai clar: Reelul e video prin definitie, iar
// functia cere adresa videoului; fara ea, pica.
const CERE_VIDEO = ['facebook|Reel', 'instagram|Reel', 'tiktok|Video'];
const CERE_IMAGINI = ['tiktok|Photo Mode', 'instagram|Carusel'];

// Singurul format care poate pleca numai cu text. Toate celelalte au nevoie de
// media: un Reel fara video sau un Story fara imagine n-are ce publica.
// Textele de lucru n-au media deloc: pe cardul lor nici nu se deseneaza galeria.
// Fara ele aici, fiecare ar purta un „lipseste media" rosu, degeaba.
const POATE_FARA_MEDIA = [
  // Threads e, in primul rand, text: o postare fara poza e cazul obisnuit acolo.
  'facebook|Feed', 'linkedin|Feed', 'threads|Feed', 'x|Post',
  'facebook|Reel script', 'facebook|Story text',
  'instagram|Reel script', 'instagram|Planse', 'instagram|Story text',
  'tiktok|Script', 'presa|Comunicat',
];

// Cate fisiere duce formatul, de fapt. Verificat in functiile de publicare:
// facebook-publish trimite `imagine_url`, unul singur; instagram-publish la
// Story trimite `image_url`, unul singur; tiktok-publish trimite
// `photo_images: [o singura adresa]`. Doar caruselul duce mai multe.
// Fara avertismentul asta, pui trei poze, pleaca una, si nu afli niciodata.
// Extins pe 13 septembrie 2026: Facebook Feed, Instagram Feed, TikTok Photo Mode si
// LinkedIn Feed duc si ele mai multe poze. Functiile de publicare sunt scrise, dar
// NEPUSE in productie pana la dezghet; vezi comentariile din fiecare functie.
const DUCE_MAI_MULTE = ['instagram|Carusel', 'facebook|Feed', 'instagram|Feed', 'tiktok|Photo Mode', 'linkedin|Feed', 'x|Post'];

// Cate poze primeste fiecare format, cand primeste mai multe. Cifrele sunt din
// documentatia oficiala, citita pe 13 septembrie 2026: TikTok Photo Post, pana la 35;
// LinkedIn MultiImage, intre 2 si 20. Facebook NU are intrare: documentatia Pages API
// nu pomeneste postarea cu mai multe poze, deci nu stim plafonul, iar un plafon
// ghicit e mai rau decat niciunul.
const PLAFON_POZE = { 'tiktok|Photo Mode': 35, 'linkedin|Feed': 20, 'x|Post': 4 };

// Formatele care, cu mai multe fisiere, primesc DOAR imagini. La Facebook o postare
// nu poate amesteca poze si video; la LinkedIn MultiImage accepta doar imagini; la
// Instagram, Feed-ul cu mai multe poze pleaca drept carusel, iar caruselul nostru e
// doar de imagini. TikTok Photo Mode si Caruselul sunt deja in CERE_IMAGINI.
const MULTE_DOAR_IMAGINI = ['facebook|Feed', 'instagram|Feed', 'linkedin|Feed'];

// Rezolutia minima pentru un story video pe Facebook. Confirmata de doua ori:
// documentatia o spune, si am vazut-o pe viu pe 22 august — un video 464x832 a
// fost acceptat la incarcare, a raspuns „Video is Processing", si nu a aparut
// niciodata. ACELASI fisier a mers ca REEL, deci Reels sunt mai ingaduitoare la
// rezolutie si NU primesc avertismentul asta.
//
// Instagram Story n-are inca regula aici: banuiesc ca are acelasi prag, dar
// n-am dovada, iar un avertisment fals te invata sa le ignori pe toate. Se
// adauga cand se poate testa, dupa raspunsul de la Instagram.
const STORY_VIDEO_MIN_L = 540;
const STORY_VIDEO_MIN_I = 960;

// O singura definitie a regulii. Se cheama din doua locuri: o data ca sa dea
// avertismentul, o data ca sa nu mai adauge si unul de forma peste el. Cand
// ma uitam in lista de mesaje in loc sa intreb aici, raspunsul depindea de
// ordinea din bucla si iesea gresit.
function videoPreaMicPentruStory(cheie, el) {
  if (cheie !== 'facebook|Story' || el.tip !== 'video') return false;
  if (!el.latime || !el.inaltime) return false;
  return el.latime < STORY_VIDEO_MIN_L || el.inaltime < STORY_VIDEO_MIN_I;
}

export const CARUSEL_MIN = 2;
export const CARUSEL_MAX = 10;

export function avertismenteMedia(cheie, elemente) {
  const [canal, format] = String(cheie).split('|');
  const lista = elemente || [];
  const mesaje = [];

  if (lista.length === 0) {
    if (!POATE_FARA_MEDIA.includes(cheie)) {
      mesaje.push({ fel: 'blocant', cod: 'faraMedia' });
    }
    return mesaje;
  }

  if (lista.length > 1 && !DUCE_MAI_MULTE.includes(cheie)) {
    mesaje.push({ fel: 'atentie', cod: 'doarPrima', n: lista.length, format });
  }

  // Formatul cere un anumit fel de media.
  if (CERE_VIDEO.includes(cheie) && !lista.some(el => el.tip === 'video')) {
    mesaje.push({ fel: 'blocant', cod: 'cereVideo', format });
  }
  if (CERE_IMAGINI.includes(cheie) && lista.some(el => el.tip === 'video')) {
    mesaje.push({ fel: 'blocant', cod: 'cereImagini', format });
  }

  if (canal === 'instagram' && format === 'Carusel') {
    if (lista.length < CARUSEL_MIN) mesaje.push({ fel: 'blocant', cod: 'caruselPreaPutine', n: lista.length });
    if (lista.length > CARUSEL_MAX) mesaje.push({ fel: 'blocant', cod: 'caruselPreaMulte', n: lista.length });
    // Instagram aplica raportul PRIMEI imagini la toate. Amestecul se taie.
    const rapoarte = lista.filter(el => el.raport).map(el => el.raport.toFixed(2));
    if (new Set(rapoarte).size > 1) mesaje.push({ fel: 'atentie', cod: 'caruselRapoarteDiferite' });
  }

  // Feed-ul de Instagram cu mai multe poze pleaca AUTOMAT drept carusel, decis de
  // Luminita pe 13 septembrie 2026. Deci ii aplicam regulile caruselului, si ii
  // spunem omului ce se intampla, fiindca pe card scrie „Feed", nu „Carusel".
  if (canal === 'instagram' && format === 'Feed' && lista.length > 1) {
    mesaje.push({ fel: 'info', cod: 'feedDevineCarusel', n: lista.length });
    if (lista.length > CARUSEL_MAX) mesaje.push({ fel: 'blocant', cod: 'caruselPreaMulte', n: lista.length });
    const rapoarteFeed = lista.filter(el => el.raport).map(el => el.raport.toFixed(2));
    if (new Set(rapoarteFeed).size > 1) mesaje.push({ fel: 'atentie', cod: 'caruselRapoarteDiferite' });
  }

  if (PLAFON_POZE[cheie] && lista.length > PLAFON_POZE[cheie]) {
    mesaje.push({ fel: 'blocant', cod: 'preaMultePoze', n: lista.length, max: PLAFON_POZE[cheie], format });
  }

  if (lista.length > 1 && MULTE_DOAR_IMAGINI.includes(cheie) && lista.some(el => el.tip === 'video')) {
    mesaje.push({ fel: 'blocant', cod: 'multeDoarImagini', format });
  }

  for (const el of lista) {
    // Apare doar la comprimare dura (vezi `pregatesteFisier`). Micsorarea
    // obisnuita e tacuta.
    if (el.micsorata) {
      mesaje.push({
        fel: 'atentie', cod: 'micsorata', nume: el.nume,
        de: el.micsorata.de.toFixed(1), la: el.micsorata.la.toFixed(1),
      });
    }

    // Forma se verifica si la VIDEO, dar numai pe Story.
    //
    // Gasit pe 22 august, cu filmul de proba: verificarea era doar pe poze, deci
    // un video orizontal de 1920x1080 pus pe Story trecea complet tacut. E destul
    // de mare ca sa treaca de pragul de rezolutie si totusi e culcat pe un ecran
    // vertical — ori apare cu benzi negre sus si jos, ori i se taie capetele.
    //
    // Pe Feed nu extind verificarea la video: nu stiu pe de rost intervalele
    // acceptate pentru video la Facebook si Instagram, iar un avertisment ghicit
    // e mai rau decat niciunul. Se adauga dupa ce le verific.
    //
    // Si nu trimit `id` la video: `id` face sa apara butonul „Potriveste", care
    // deseneaza pe o panza — merge la poze, nu la filme. Un buton care nu poate
    // functiona e mai rau decat lipsa lui.
    const formaPoza = el.tip === 'imagine' ? RAPORT[cheie] : null;
    const formaVideo = el.tip === 'video' && RAPORT[cheie] && RAPORT[cheie].ecranPlin
      ? RAPORT[cheie] : null;

    if (formaVideo && el.raport) {
      const preaMic = videoPreaMicPentruStory(cheie, el);
      const abatere = Math.abs(el.raport - formaVideo.ecranPlin) / formaVideo.ecranPlin;
      // Daca deja i-am spus ca filmul e prea mic, nu mai adaug si forma: tot
      // alt fisier trebuie sa puna. Doua mesaje pentru aceeasi decizie e zgomot.
      if (abatere > TOLERANTA_ECRAN_PLIN && !preaMic) {
        mesaje.push({
          fel: 'atentie', cod: 'videoNuUmpleEcranul',
          nume: el.nume, raport: el.raport.toFixed(2),
        });
      }
    }

    const forma = formaPoza;
    if (forma && el.raport) {
      const raport = el.raport.toFixed(2);
      if (forma.ecranPlin) {
        const abatere = Math.abs(el.raport - forma.ecranPlin) / forma.ecranPlin;
        if (abatere > TOLERANTA_ECRAN_PLIN) {
          mesaje.push({ fel: 'atentie', cod: 'nuUmpleEcranul', nume: el.nume, raport, id: el.id });
        }
      } else if (el.raport < forma.min) {
        mesaje.push({ fel: 'atentie', cod: 'taieSusJos', nume: el.nume, raport, canal, id: el.id });
      } else if (el.raport > forma.max) {
        mesaje.push({ fel: 'atentie', cod: 'taieLaterale', nume: el.nume, raport, canal, id: el.id });
      }
    }
    if (videoPreaMicPentruStory(cheie, el)) {
      mesaje.push({ fel: 'blocant', cod: 'storyVideoPreaMic', l: el.latime, i: el.inaltime });
    }
    // Depozitul opreste orice fisier, poza sau film. Se verifica primul, pentru
    // ca e plafonul cel mai jos: n-are rost sa spui „Instagram accepta 100 MB"
    // cand fisierul nici nu iese din casa.
    //
    // Un LINK nu trece prin depozit — asta e tot rostul lui — deci plafonul de
    // 50 MB nu i se aplica. Daca l-as aplica si aici, as anula fix lucrul
    // pentru care exista linkurile.
    if (!el.esteLink && el.marimeMB > LIMITA_DEPOZIT_MB) {
      mesaje.push({
        fel: 'blocant', cod: 'pesteDepozit',
        nume: el.nume, mb: Math.round(el.marimeMB), limita: LIMITA_DEPOZIT_MB,
      });
    } else if (el.tip === 'video') {
      const limita = LIMITA_VIDEO_MB[canal];
      if (limita && el.marimeMB > limita) {
        mesaje.push({ fel: 'blocant', cod: 'videoPreaMare', nume: el.nume, mb: Math.round(el.marimeMB), limita });
      }
    }
    // Un link pe care nu l-am putut masura ramane folosibil: publicarea nu trece
    // prin browserul nostru, deci Facebook tot il ia. Dar verificarile de forma,
    // rezolutie si durata NU s-au facut, si omul trebuie sa stie ca merge fara
    // plasa — nu sa creada ca tacerea inseamna „e in regula".
    if (el.esteLink && !el.masurat) {
      mesaje.push({ fel: 'atentie', cod: 'linkNemasurat', nume: el.nume });
    }
    if (el.tip === 'video' && canal === 'tiktok' && el.marimeMB > LIMITA_TIKTOK_MEMORIE_MB) {
      mesaje.push({
        fel: 'atentie', cod: 'tiktokMemorie',
        mb: Math.round(el.marimeMB), limita: LIMITA_TIKTOK_MEMORIE_MB,
      });
    }
    if (el.eroare) mesaje.push({ fel: 'blocant', cod: 'nuAUrcat', nume: el.nume });
  }

  return mesaje;
}

// ============================================================
// COPERTA FILMULUI
//
// Ce miniatura se vede inainte sa porneasca filmul. Pana acum o alegea reteaua,
// adica un cadru luat la intamplare: in grila clientului aparea o fata cu gura
// deschisa, nu cartonul casei.
//
// Retelele nu primesc acelasi lucru, si asta decide toata interfata:
//   Instagram — poza (`cover_url`) SAU secunda (`thumb_offset`)
//   Facebook  — DOAR poza, si nu la publicare: un al doilea apel, dupa ce
//               filmul a urcat, la /{video_id}/thumbnails
//   TikTok    — DOAR secunda (`video_cover_timestamp_ms`)
// Precedenta, peste tot la fel: daca e poza, castiga poza; altfel secunda;
// altfel alege reteaua, ca pana acum.
// ============================================================

// Cardurile care duc o coperta. Story-ul nu: e ecran plin, se vede filmul de la
// primul cadru. „Reel script" nu e aici fiindca nu e o postare, e text de lucru
// — de aia se cere cheia INTREAGA, niciodata substringul „Reel".
export const DUCE_COPERTA = [
  'facebook|Feed', 'facebook|Reel',
  'instagram|Feed', 'instagram|Reel',
  // 'tiktok|Video' a fost SCOS pe 20 septembrie 2026, dupa patru probe pe viu: trimiteam
  // `video_cover_timestamp_ms` corect, in `post_info`, cu valori bine inauntrul filmului, iar
  // TikTok lua de fiecare data alt cadru. Toate patru erau postari private („Doar eu"), singurele
  // permise inainte de auditul Direct Post. PUS LA LOC pe 13 octombrie 2026, dupa audit (aprobat
  // 24.09), si PROBAT pe o postare publica pe contul Jackpot: secunda 4,5 ceruta, cadrul de la 4,5
  // aparut in grila. Deci TikTok nu aplica coperta pe postarile private ale aplicatiilor neauditate.
  'tiktok|Video',
];

export function feluriCoperta(cheie) {
  if (!DUCE_COPERTA.includes(cheie)) return { poza: false, secunda: false };
  const canal = String(cheie).split('|')[0];
  if (canal === 'facebook') return { poza: true, secunda: false };
  if (canal === 'tiktok') return { poza: false, secunda: true };
  return { poza: true, secunda: true };
}

export function areCoperta(coperta) {
  const c = coperta || {};
  return !!(c.url || c.previzualizare || Number.isFinite(c.ms));
}

// Cat poate sa difere forma copertei de a filmului inainte sa se vada. Peste
// 10% reteaua o decupeaza, si atunci se pierde exact ce conteaza: titlul scris
// pe carton.
const TOLERANTA_FORMA_COPERTA = 0.1;

// Aceeasi forma de mesaje ca `avertismenteMedia`, si se lipesc in aceeasi lista
// pe card. Functie pura: cheia, coperta si media cardului.
export function avertismenteCoperta(cheie, coperta, elemente) {
  const mesaje = [];
  const c = coperta || {};
  const video = (elemente || []).find(el => el.tip === 'video');
  // Fara film nu exista coperta de pus, iar cardul nici nu arata blocul.
  if (!video || !areCoperta(c)) return mesaje;

  const feluri = feluriCoperta(cheie);
  const canal = String(cheie).split('|')[0];

  const arePoza = !!c.url;
  const areSecunda = Number.isFinite(c.ms);

  // Avertismentul sare DOAR cand reteaua ar ramane fara nimic: ce s-a pus nu
  // poate fi dus de ea SI nu exista si celalalt fel. Cand cadrul e ales din film
  // pleaca si poza, si secunda, deci nimanui nu-i lipseste nimic si pe card e
  // liniste. Prima varianta se uita doar la ce s-a pus, nu si la ce lipseste,
  // deci striga pe cardul de TikTok chiar si cand secunda era acolo (gasit de
  // Luminita pe viu, 19 sept 2026).
  if (arePoza && !areSecunda && !feluri.poza) {
    mesaje.push({ fel: 'atentie', cod: 'copertaCereSecunda', canal });
  }
  if (areSecunda && !arePoza && !feluri.secunda) {
    mesaje.push({ fel: 'atentie', cod: 'copertaCerePoza', canal });
  }
  if (c.eroare) mesaje.push({ fel: 'atentie', cod: 'copertaNuAUrcat' });

  if (c.url && feluri.poza && c.raport && video.raport) {
    const diferenta = Math.abs(c.raport - video.raport) / video.raport;
    if (diferenta > TOLERANTA_FORMA_COPERTA) {
      mesaje.push({ fel: 'atentie', cod: 'copertaAltaForma' });
    }
  }
  // Secunda de dupa sfarsitul filmului conteaza doar daca reteaua chiar o
  // foloseste: cu o poza pe care STIE s-o ia, secunda nici nu pleaca.
  const seFoloseste = areSecunda && !(arePoza && feluri.poza);
  if (seFoloseste && video.durataSec && c.ms / 1000 > video.durataSec) {
    mesaje.push({
      fel: 'atentie', cod: 'copertaPesteDurata',
      s: Math.round(c.ms / 100) / 10, durata: Math.round(video.durataSec * 10) / 10,
    });
  }
  return mesaje;
}

// ============================================================
// „Potriveste": aduce imaginea la forma ceruta FARA sa taie din ea.
//
// Nu taie si nu intinde. Pune poza intreaga la mijloc si umple restul cu o
// versiune marita si neclara a aceleiasi poze. Benzile ies acolo unde cadrul e
// mai mare decat poza: la o poza prea INALTA, in lateral; la una prea LATA, sus
// si jos. (Taierea face pe dos, si vine separat, la finalul reconstructiei.)
//
// De ce blur si nu alb sau negru: benzile albe taie feedul si arata a greseala,
// iar blurul din poza insasi pastreaza culorile si se citeste ca o rama.
// ============================================================

const LATURA_MAXIMA = 2048;

// Ce forma ar trebui sa aiba imaginea pe cheia asta. null = e deja buna.
// Pentru un interval se alege MARGINEA CEA MAI APROPIATA, nu mijlocul: asa poza
// ramane cat mai mare pe ecran. O poza inalta dusa la 4:5 pastreaza mult mai
// mult decat una dusa la patrat.
export function formaTinta(cheie, raport) {
  const forma = RAPORT[cheie];
  if (!forma || !raport) return null;
  if (forma.ecranPlin) {
    return Math.abs(raport - forma.ecranPlin) / forma.ecranPlin > TOLERANTA_ECRAN_PLIN ? forma.ecranPlin : null;
  }
  if (raport < forma.min) return forma.min;
  if (raport > forma.max) return forma.max;
  return null;
}

// Rama retelei, pentru previzualizare. `formaTinta` spune CE forma trebuie sa
// aiba poza; asta spune CAT se pierde si din ce parte, ca lupa sa poata desena
// peste imagine exact ce nu va incapea.
//
// `procent` e cat se taie de FIECARE parte, ca fractie din latura respectiva a
// pozei: 0.12 inseamna 12% din latime in stanga si 12% in dreapta. Intoarce null
// cand nu se taie nimic — inclusiv pentru TikTok si pentru orice cheie fara
// intrare in RAPORT, unde nu stim intervalul si nu inventam unul.
export function ramaRetea(cheie, raport) {
  const forma = RAPORT[cheie];
  if (!forma || !raport) return null;
  let tinta;
  if (forma.ecranPlin) {
    if (Math.abs(raport - forma.ecranPlin) / forma.ecranPlin <= TOLERANTA_ECRAN_PLIN) return null;
    tinta = forma.ecranPlin;
  } else if (raport < forma.min) {
    tinta = forma.min;
  } else if (raport > forma.max) {
    tinta = forma.max;
  } else {
    return null;
  }
  if (raport > tinta) return { taie: 'laterale', tinta, procent: (1 - tinta / raport) / 2 };
  return { taie: 'susJos', tinta, procent: (1 - raport / tinta) / 2 };
}

export function potriveste(fisier, tinta) {
  return new Promise((rezolva, respinge) => {
    const obiectUrl = URL.createObjectURL(fisier);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(obiectUrl);
      const L = img.naturalWidth, I = img.naturalHeight;
      const raport = L / Math.max(1, I);

      // Cadrul pastreaza latura care deja incape si o mareste pe cealalta.
      let cadruL, cadruI;
      if (raport < tinta) { cadruI = I; cadruL = Math.round(I * tinta); }
      else { cadruL = L; cadruI = Math.round(L / tinta); }

      const scara = Math.min(1, LATURA_MAXIMA / Math.max(cadruL, cadruI));
      cadruL = Math.max(1, Math.round(cadruL * scara));
      cadruI = Math.max(1, Math.round(cadruI * scara));

      const canvas = document.createElement('canvas');
      canvas.width = cadruL; canvas.height = cadruI;
      const ctx = canvas.getContext('2d');

      // 1. Fundalul: aceeasi poza, marita cat sa acopere tot cadrul, neclara.
      //    Se deseneaza cu 10% mai mare, ca marginile blurate sa nu lase dungi.
      const acopera = Math.max(cadruL / L, cadruI / I) * 1.1;
      const fL = L * acopera, fI = I * acopera;
      ctx.filter = 'blur(24px)';
      ctx.drawImage(img, (cadruL - fL) / 2, (cadruI - fI) / 2, fL, fI);
      ctx.filter = 'none';

      // 2. Poza intreaga, la mijloc, cat de mare incape.
      const incape = Math.min(cadruL / L, cadruI / I);
      const pL = L * incape, pI = I * incape;
      ctx.drawImage(img, (cadruL - pL) / 2, (cadruI - pI) / 2, pL, pI);

      canvas.toBlob(blob => {
        if (!blob) { respinge(new Error('potrivire eșuată')); return; }
        const nume = fisier.name.replace(/\.[^.]+$/, '') + '-potrivit.jpg';
        rezolva(new File([blob], nume, { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(obiectUrl); respinge(new Error('imagine ilizibilă')); };
    img.src = obiectUrl;
  });
}
